import * as THREE from 'three';
import { useMapStore } from '@/stores/mapStore.ts'
import { logger } from '@/models/logger';

// Fixed crosshair properties (constant visual size)
const CROSSHAIR_SIZE = 3;
const CROSSHAIR_GAP = 1.5;
const CROSSHAIR_THICKNESS = 0.2;
const CROSSHAIR_COLOR = 0x333333;

/**
 * Draw a crossHair in the center of the screen
 */
export class MapCrossHairInit {
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {

    const crossHairGroup = new THREE.Group();
    crossHairGroup.name = 'crossHair';
    crossHairGroup.position.set(0, 0, -100);
    camera.add(crossHairGroup);
    scene.add(camera);

    // Initial zoom level setup
    new MapCrossHairUpdate(scene, 1);
  }
}

/**
 * Update the crossHair based on the zoom level
 * Scales inversely to zoom to maintain constant visual size
 */
export class MapCrossHairUpdate {
  constructor(scene: THREE.Scene, zoomLevel: number) {
    // Find the crossHair group by name
    const crossHairGroup = scene.getObjectByName('crossHair') as THREE.Group;

    if (!crossHairGroup) {
      logger.error('CrossHair group not found');
      return;
    }

    const mapStore = useMapStore();

    if (!mapStore.crossHairVisible) {
      crossHairGroup.visible = false;
    } else {
      // Clear existing crossHair components
      crossHairGroup.clear();

      const crossHairMaterial = new THREE.MeshBasicMaterial({
        color: CROSSHAIR_COLOR,
        depthTest: false, // Ensure crossHair stays visible in front of everything
      });

      // Create crossHair geometries with fixed size
      const hLineLeftGeometry = new THREE.PlaneGeometry(CROSSHAIR_SIZE - CROSSHAIR_GAP, CROSSHAIR_THICKNESS);
      const hLineLeft = new THREE.Mesh(hLineLeftGeometry, crossHairMaterial);
      hLineLeft.position.set(-(CROSSHAIR_SIZE + CROSSHAIR_GAP) / 2, 0, 0);
      crossHairGroup.add(hLineLeft);

      const hLineRightGeometry = new THREE.PlaneGeometry(CROSSHAIR_SIZE - CROSSHAIR_GAP, CROSSHAIR_THICKNESS);
      const hLineRight = new THREE.Mesh(hLineRightGeometry, crossHairMaterial);
      hLineRight.position.set((CROSSHAIR_SIZE + CROSSHAIR_GAP) / 2, 0, 0);
      crossHairGroup.add(hLineRight);

      const vLineTopGeometry = new THREE.PlaneGeometry(CROSSHAIR_THICKNESS, CROSSHAIR_SIZE - CROSSHAIR_GAP);
      const vLineTop = new THREE.Mesh(vLineTopGeometry, crossHairMaterial);
      vLineTop.position.set(0, (CROSSHAIR_SIZE + CROSSHAIR_GAP) / 2, 0);
      crossHairGroup.add(vLineTop);

      const vLineBottomGeometry = new THREE.PlaneGeometry(CROSSHAIR_THICKNESS, CROSSHAIR_SIZE - CROSSHAIR_GAP);
      const vLineBottom = new THREE.Mesh(vLineBottomGeometry, crossHairMaterial);
      vLineBottom.position.set(0, -(CROSSHAIR_SIZE + CROSSHAIR_GAP) / 2, 0);
      crossHairGroup.add(vLineBottom);

      // Scale inversely to zoom so crosshair maintains constant visual size
      const scale = 1 / zoomLevel;
      crossHairGroup.scale.set(scale, scale, 1);

      crossHairGroup.visible = true;
    }
  }
}
